/**
 * Utility function to export Firestore documents to CSV
 * Flattens nested objects and arrays for easy viewing in spreadsheet applications
 */

/**
 * Flattens a nested object into a flat object with dot-notation keys
 * Example: { links: { fs_ids: ['FS-001'] } } becomes { 'links.fs_ids': 'FS-001' }
 */
function flattenObject(obj: any, prefix = '', result: Record<string, any> = {}): Record<string, any> {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value === null || value === undefined) {
        result[newKey] = '';
      } else if (value instanceof Date) {
        result[newKey] = value.toISOString();
      } else if (Array.isArray(value)) {
        // Join arrays with semicolon for CSV readability
        result[newKey] = value.map(item => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(item);
          }
          return String(item);
        }).join('; ');
      } else if (typeof value === 'object') {
        // Recursively flatten nested objects
        flattenObject(value, newKey, result);
      } else {
        result[newKey] = value;
      }
    }
  }
  return result;
}

/**
 * Escapes a CSV field value
 * Handles commas, quotes, and newlines
 */
function escapeCSVField(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  const stringValue = String(value);
  
  // If the value contains comma, quote, or newline, wrap it in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

/**
 * Converts an array of objects to CSV format
 */
function convertToCSV(data: any[]): string {
  if (data.length === 0) {
    return '';
  }

  // Flatten all objects and collect all possible keys
  const flattenedData = data.map(item => {
    // Remove _firestoreId as it's an internal field
    const { _firestoreId, ...itemWithoutFirestoreId } = item;
    return flattenObject(itemWithoutFirestoreId);
  });

  // Get all unique keys from all flattened objects
  const allKeys = new Set<string>();
  flattenedData.forEach(item => {
    Object.keys(item).forEach(key => allKeys.add(key));
  });

  // Sort keys for consistent column order
  const sortedKeys = Array.from(allKeys).sort();

  // Create CSV header
  const header = sortedKeys.map(key => escapeCSVField(key)).join(',');

  // Create CSV rows
  const rows = flattenedData.map(item => {
    return sortedKeys.map(key => escapeCSVField(item[key] || '')).join(',');
  });

  // Combine header and rows
  return [header, ...rows].join('\n');
}

/**
 * Downloads a CSV file
 */
function downloadCSV(csvContent: string, filename: string): void {
  // Add BOM for UTF-8 to help Excel recognize encoding
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Exports an array of Firestore documents to CSV
 * 
 * @param data - Array of Firestore documents (can be any objects)
 * @param filename - Name of the CSV file (without .csv extension)
 */
export function exportToCSV<T extends Record<string, any>>(data: T[], filename: string): void {
  if (data.length === 0) {
    throw new Error('Cannot export empty array to CSV');
  }

  const csvContent = convertToCSV(data);
  const csvFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  downloadCSV(csvContent, csvFilename);
}
